import * as React from 'react'

import { BLOCK_ELEMENTS, WHITESPACE_PATTERN, WordCollector, applyPronunciationOverrides, createWordCollector, speechTextFromWords, spokenPhrase } from './wordCollector'

import { safeParseHtml } from '../safeHtml'

export interface WordTimeslot {
  end: number;
  start: number;
}

const UNSPOKEN_ELEMENTS = ['script', 'style']
export const SPOKEN_WORD_ATTRIBUTE = 'data-read-aloud-word'
const SCREENREADER_ONLY_CLASS = 'sr-only'
export const UNSPOKEN_CLASS = 'read-aloud-unspoken'

const hasClass = (className: unknown, name: string) => typeof className === 'string' && className.split(' ').includes(name)

export const isSpeakableReactElement = (element: React.ReactElement) => {
  if (typeof element.type === 'string' && UNSPOKEN_ELEMENTS.includes(element.type)) { return false }

  const props = element.props as { className?: unknown }
  return !hasClass(props?.className, SCREENREADER_ONLY_CLASS) && !hasClass(props?.className, UNSPOKEN_CLASS)
}

export const spokenWordOf = (element: React.ReactElement) => {
  const word = (element.props as Record<string, unknown>)[SPOKEN_WORD_ATTRIBUTE]
  return typeof word === 'string' && word ? word : null
}

export const isSpeakableTextNode = (domNode: any) => {
  if (domNode.type !== 'text' || !domNode.data) { return false }

  let node = domNode.parent
  while (node) {
    if (UNSPOKEN_ELEMENTS.includes(node.name) || hasClass(node.attribs?.class, SCREENREADER_ONLY_CLASS) || hasClass(node.attribs?.class, UNSPOKEN_CLASS)) { return false }
    node = node.parent
  }
  return true
}

export const blockAncestorOf = (domNode: any, fallback: unknown = null) => {
  let node = domNode.parent
  while (node) {
    if (BLOCK_ELEMENTS.includes(node.name)) { return node }
    node = node.parent
  }
  return fallback
}

const isBlockElement = (element: React.ReactElement) => typeof element.type === 'string' && BLOCK_ELEMENTS.includes(element.type)

export function collectHtmlWords(html: string, collector?: WordCollector): string[] {
  const entry = !collector
  const wordCollector = collector || createWordCollector()
  const enclosingBlock = wordCollector.currentBlock
  const collectTextNode = (domNode: any) => {
    if (isSpeakableTextNode(domNode)) {
      wordCollector.setBlock(blockAncestorOf(domNode, enclosingBlock))
      wordCollector.tokenize(domNode.data)
    }
    return undefined
  }
  safeParseHtml(html, { replace: collectTextNode, })
  wordCollector.setBlock(enclosingBlock)
  return entry ? wordCollector.finish() : wordCollector.words
}

const collectNodeWordsInto = (node: React.ReactNode, collector: WordCollector): void => {
  if (node == null || typeof node === 'boolean') { return }
  if (typeof node === 'string' || typeof node === 'number') {
    collector.tokenize(String(node))
    return
  }
  if (Array.isArray(node)) {
    node.forEach(child => collectNodeWordsInto(child, collector))
    return
  }
  if (!React.isValidElement(node) || !isSpeakableReactElement(node)) { return }

  const spokenWord = spokenWordOf(node)
  if (spokenWord) {
    collector.tokenize(spokenWord)
    return
  }

  const enclosingBlock = collector.currentBlock
  if (isBlockElement(node)) { collector.setBlock(node) }
  const props = node.props as { children?: React.ReactNode, dangerouslySetInnerHTML?: { __html?: string } }
  if (props?.dangerouslySetInnerHTML?.__html) {
    collectHtmlWords(props.dangerouslySetInnerHTML.__html, collector)
  } else {
    collectNodeWordsInto(props?.children, collector)
  }
  collector.setBlock(enclosingBlock)
}

export function collectNodeWords(node: React.ReactNode, collector?: WordCollector): string[] {
  const entry = !collector
  const wordCollector = collector || createWordCollector()
  collectNodeWordsInto(node, wordCollector)
  return entry ? wordCollector.finish() : wordCollector.words
}

export const promptToSpeechText = (prompt: string | null | undefined) => speechTextFromWords(collectHtmlWords(prompt || ''))

export const reactNodeToSpeechText = (node: React.ReactNode) => speechTextFromWords(collectNodeWords(node))

export interface CueSpeechSegment {
  cueIndex: number | null;
  text: string;
}

const CONNECTOR = 'or'

export function cueSpeechSegments(cues: string[] = [], cuesLabel?: string): CueSpeechSegment[] {
  const presentCues = cues.map(cue => spokenPhrase((cue || '').trim())).filter(Boolean)
  if (!presentCues.length) { return [] }

  const label = cuesLabel && cuesLabel.trim() ? spokenPhrase(cuesLabel.trim()) : (presentCues.length === 1 ? 'joining word' : 'joining words')
  const last = presentCues.length - 1
  const segments: CueSpeechSegment[] = [{ cueIndex: null, text: `${label}:`, }]
  presentCues.forEach((cue, index) => {
    if (index === last && last > 0) { segments.push({ cueIndex: null, text: CONNECTOR, }) }
    const separator = index === last ? '.' : (presentCues.length > 2 ? ',' : '')
    segments.push({ cueIndex: index, text: `${cue}${separator}`, })
  })
  return segments
}

export const cuesToSpeechText = (cues: string[] = [], cuesLabel?: string) => applyPronunciationOverrides(cueSpeechSegments(cues, cuesLabel).map(segment => segment.text).join(' '))

export function cueSegmentIndexForWord(segments: CueSpeechSegment[], wordIndex: number): number | null {
  let seen = 0
  for (let index = 0; index < segments.length; index += 1) {
    seen += segments[index].text.split(WHITESPACE_PATTERN).filter(Boolean).length
    if (wordIndex < seen) { return index }
  }
  return null
}

export function buildWordTimeline(speechText: string): WordTimeslot[] {
  const timeline: WordTimeslot[] = []
  const wordPattern = /\S+/g
  let match = wordPattern.exec(speechText)
  while (match !== null) {
    timeline.push({ end: match.index + match[0].length, start: match.index, })
    match = wordPattern.exec(speechText)
  }
  return timeline
}

export function wordIndexForCharIndex(timeline: WordTimeslot[], charIndex: number): number | null {
  const index = timeline.findIndex(slot => charIndex < slot.end)
  return index === -1 ? null : index
}
